/**
 * Lady Milan - Category Showcase Slider Web Component
 * Pure Vanilla JavaScript implementation with 10s auto-slide, hover/click thumbnail switching,
 * touch swipe, and Shopify Theme Editor integration.
 */

if (!customElements.get('lm-showcase-slider')) {
  class LmShowcaseSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoSlideInterval = 10000; // 10 seconds
      this.timer = null;
      this.hoverTimeout = null;
      this.isPaused = false;
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.lm-showcase-slide'));
      this.thumbs = Array.from(this.querySelectorAll('.lm-showcase-thumb'));
      this.heroContainer = this.querySelector('.lm-showcase-hero-stage');
      this.thumbsContainer = this.querySelector('.lm-showcase-thumbs-track');

      if (!this.slides.length || !this.thumbs.length) return;

      this.initEvents();
      this.goTo(0, false);
      this.startTimer();
    }

    disconnectedCallback() {
      this.stopTimer();
    }

    initEvents() {
      // Thumbnail Hover & Click Events
      this.thumbs.forEach((thumb, index) => {
        // Hover: debounced 50ms for smooth instantaneous response without jitter
        thumb.addEventListener('mouseenter', () => {
          clearTimeout(this.hoverTimeout);
          this.hoverTimeout = setTimeout(() => {
            this.goTo(index, true);
          }, 50);
        });

        thumb.addEventListener('mouseleave', () => {
          clearTimeout(this.hoverTimeout);
        });

        // Click
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.goTo(index, true);
        });

        // Keyboard accessibility (Enter or Space)
        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.goTo(index, true);
          }
        });
      });

      // Pause timer when hovering over the main hero stage or button
      if (this.heroContainer) {
        this.heroContainer.addEventListener('mouseenter', () => {
          this.isPaused = true;
        });

        this.heroContainer.addEventListener('mouseleave', () => {
          this.isPaused = false;
        });

        // Touch Swipe Handling on Mobile
        this.heroContainer.addEventListener('touchstart', (e) => {
          this.touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        this.heroContainer.addEventListener('touchend', (e) => {
          this.touchEndX = e.changedTouches[0].screenX;
          this.handleSwipe();
        }, { passive: true });
      }

      // Shopify Theme Editor Integration
      document.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail.blockId;
        const targetIndex = this.slides.findIndex(s => s.dataset.blockId === blockId);
        if (targetIndex !== -1) {
          this.goTo(targetIndex, true);
        }
      });
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchEndX - this.touchStartX;
      if (diff < -swipeThreshold) {
        // Swipe Left -> Next slide
        this.next();
      } else if (diff > swipeThreshold) {
        // Swipe Right -> Prev slide
        this.prev();
      }
    }

    goTo(index, resetTimer = true) {
      if (index < 0) index = this.slides.length - 1;
      if (index >= this.slides.length) index = 0;

      this.currentIndex = index;

      // Update Slides state
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', !isActive);
      });

      // Update Thumbnails state
      this.thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive);
      });

      // Scroll thumbnail track horizontally without jumping the browser window
      if (this.thumbsContainer && this.thumbs[index]) {
        const container = this.thumbsContainer;
        const activeThumb = this.thumbs[index];
        if (container.scrollWidth > container.clientWidth) {
          const scrollTarget = activeThumb.offsetLeft - (container.clientWidth / 2) + (activeThumb.offsetWidth / 2);
          container.scrollTo({
            left: Math.max(0, scrollTarget),
            behavior: 'smooth'
          });
        }
      }

      if (resetTimer) {
        this.restartTimer();
      }
    }

    next() {
      this.goTo(this.currentIndex + 1, true);
    }

    prev() {
      this.goTo(this.currentIndex - 1, true);
    }

    startTimer() {
      this.stopTimer();
      this.timer = setInterval(() => {
        if (!this.isPaused) {
          this.next();
        }
      }, this.autoSlideInterval);
    }

    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartTimer() {
      this.stopTimer();
      this.startTimer();
    }
  }

  customElements.define('lm-showcase-slider', LmShowcaseSlider);
}
